import { useState, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import { useNavigate } from "@/lib/router-compat";

import { Database } from "@/integrations/supabase/types";

// Eager load critical card components to prevent double skeleton flash
// Table view is opt-in, so its row component ships as its own chunk.
const CardTableRow = lazy(() => import("@/components/CardTableRow").then(m => ({ default: m.CardTableRow })));
import { CardCard } from "@/components/CardCard";
import { HeroSplitScreen } from "@/components/HeroSplitScreen";
import { HeroSplitScreenCondensed } from "@/components/HeroSplitScreenCondensed";

// Lazy load non-critical components for better code splitting
const CompareDialog = lazy(() => import("@/components/CompareDialog").then(m => ({ default: m.CompareDialog })));
const CompareDialogDE = lazy(() => import("@/components/CompareDialogDE"));
const Footer = lazy(() => import("@/components/Footer").then(m => ({ default: m.Footer })));
const HomepageFAQ = lazy(() => import("@/components/HomepageFAQ").then(m => ({ default: m.HomepageFAQ })));
const ModernFilter = lazy(() => import("@/components/ModernFilter").then(m => ({ default: m.ModernFilter })));
const HomepageContentSection = lazy(() => import("@/components/HomepageContentSection").then(m => ({ default: m.HomepageContentSection })));

const CompareBar = lazy(() => import("@/components/CompareBar").then(m => ({ default: m.CompareBar })));
const SectionRenderer = lazy(() => import("@/components/SectionRenderer").then(m => ({ default: m.SectionRenderer })));
import { Header } from "@/components/Header";
import { HomepageComparisonTable } from "@/components/HomepageComparisonTable";
import { useAIAssistant } from "@/contexts/AIAssistantContext";
import { useComparison } from "@/hooks/useComparison";
import { adaptSupabaseCardToCompareCard } from "@/lib/cardAdapter";
import { adaptSupabaseCardToGermanCompareCard } from "@/lib/cardAdapterDE-Comparison";
import { normalizeDbCard, toTableRowVM, toListCardVM } from "@/lib/cardCoreNormalizer";
import { toHeroCardVM } from "@/lib/heroCardAdapter";
import { trackAffiliateClick } from "@/lib/analytics";
import { trackAffiliateClickGTM } from "@/lib/gtm";
import { useAffiliateRedirect } from "@/hooks/useAffiliateRedirect";
import { CreditCard } from "@/types/card";
import { GermanCompareCard } from "@/types/cardDE";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, MessageSquare, ChevronDown, SlidersHorizontal } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { debounce } from "@/lib/utils";
import { CardSkeleton } from "@/components/CardSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { useCards, cardsPreviewQueryOptions, homepageCardFactsQueryOptions, publishedCardCountQueryOptions } from "@/hooks/useCards";
import { useAdPlacements } from "@/hooks/useAdPlacements";
import { useCardOverrides } from "@/hooks/useCardOverrides";
import { useOffers } from "@/hooks/useOffers";
import { useFeedSettings } from "@/hooks/useFeedSettings";
import { useHomepageSettings } from "@/hooks/useHomepageSettings";
import { useHomepageContent } from "@/hooks/useHomepageContent";
import { HomepageNavigation } from "@/components/HomepageNavigation";
import { computeChipCounts, computeCardTypeCoverage, getAvailableIssuers } from '@/lib/facets';
import type { FilterState } from '@/lib/facets';
import { useCardListingSettings } from "@/hooks/useCardListingSettings";
import { currentYear, currentMonthYearDE } from "@/lib/currentYear";
import { formatPercentDE } from "@/lib/cardCategory";
import { HOMEPAGE_FAQ } from "@/lib/homepageFaq";
import { IntentChooser } from "@/components/IntentChooser";
import { INTENTS, matchesIntents, intentScore, parseIntents, serializeIntents, type IntentKey } from "@/lib/intentFilters";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Link, useSearchParams } from "@/lib/router-compat";
import { useFirstInteraction } from "@/hooks/useFirstInteraction";

type Card = Database['public']['Tables']['cards']['Row'];

interface AdPlacement {
  card_id: string;
  priority: number;
}

const Index = () => {
  const navigate = useNavigate();
  const { openAIDialog } = useAIAssistant();
  const { handleAffiliateRedirect } = useAffiliateRedirect();
  
  // React Query hooks for data fetching with TTL caching
  // The server renders only the first cards (small HTML payload); the full
  // list arrives from the browser right after hydration and takes over.
  const { data: previewCards } = useQuery(cardsPreviewQueryOptions);
  const { data: publishedCardCount } = useQuery(publishedCardCountQueryOptions);
  const { data: homepageFacts } = useQuery(homepageCardFactsQueryOptions);
  // The full list is only fetched once the visitor actually does something.
  // Until then the page runs on the 12 server-rendered cards, so the browser
  // never pays for rendering the whole catalogue during the first paint.
  const interacted = useFirstInteraction();
  const { data: allCards, isLoading: fullCardsLoading } = useCards(interacted);
  const cards: any[] = allCards ?? previewCards ?? [];
  const cardsLoading = fullCardsLoading && !previewCards;
  const { data: adPlacements = [], isLoading: adsLoading } = useAdPlacements();
  const { data: overrides = [], isLoading: overridesLoading } = useCardOverrides();
  const { data: offers = [], isLoading: offersLoading } = useOffers();
  const { data: feedSettings, isLoading: feedLoading } = useFeedSettings();
  const { data: homepageSettings, isLoading: homepageLoading } = useHomepageSettings();
  const { data: homepageContent = [] } = useHomepageContent();
  const { data: listingSettings } = useCardListingSettings();
  
  // Detect feed type for German comparison support
  const isGerman = feedSettings?.feed_type === 'german';
  
  // Check if compact cards are enabled
  const useCompactCards = (listingSettings as any)?.use_compact_cards ?? false;
  const compactCardStyle = (listingSettings as any)?.compact_card_style ?? 'default';
  
  // Compute derived state with useMemo
  const loading = cardsLoading || adsLoading || overridesLoading || offersLoading;
  const loadingSettings = feedLoading || homepageLoading;
  
  const sponsoredCardIds = useMemo(() => 
    new Set<string>(adPlacements.map(ad => ad.card_id).filter((id): id is string => typeof id === "string")), 
    [adPlacements]
  );
  
   const sponsoredLabels = useMemo(() => {
     const map = new Map();
     adPlacements.forEach(ad => map.set(ad.card_id, ad.label || 'Anzeige'));
     return map;
   }, [adPlacements]);
  
  const offersMap = useMemo(() => {
    const map = new Map();
    offers.forEach(offer => {
      if (offer.card_id) {
        map.set(offer.card_id, offer);
      }
    });
    return map;
  }, [offers]);
  
  const promotedCardsIgnoreFilters = feedSettings?.promoted_cards_ignore_filters ?? true;
  
  const assistantSettings = useMemo(() => ({
    title: feedSettings?.assistant_hero_title || 'Kartenassistent',
    subtitle: feedSettings?.assistant_hero_subtitle || '💬 Bereits über 8.000 Nutzern geholfen',
    description: feedSettings?.assistant_hero_description || 'Beantworten Sie ein paar kurze Fragen – ich finde Karten, die zu Ihrem Alter, Einkommen und Bedarf passen.',
    buttonText: feedSettings?.assistant_hero_button_text || '🔍 Passende Karte finden',
    tooltipText: feedSettings?.assistant_tooltip_text || 'Beantworten Sie 3 Fragen – unter 30 Sekunden'
  }), [feedSettings]);
  
  const defaultCardsShown = (homepageSettings as any)?.default_cards_shown || 12;
  const showContentSections = (homepageSettings as any)?.show_content_sections ?? false;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("recommended");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [showAllCards, setShowAllCards] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // "Was ist dir wichtig?" — shareable via ?fokus=
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedIntents = useMemo(
    () => parseIntents(searchParams.get("fokus")),
    [searchParams],
  );

  const setIntents = (next: IntentKey[]) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next.length > 0) params.set("fokus", serializeIntents(next));
      else params.delete("fokus");
      return params;
    }, { replace: true });
  };

  const toggleIntent = (key: IntentKey) => {
    setIntents(
      selectedIntents.includes(key)
        ? selectedIntents.filter((k) => k !== key)
        : [...selectedIntents, key],
    );
  };

  
  
  // Advanced filters (German)
  const [advancedFilters, setAdvancedFilters] = useState({
    cardTypes: [] as string[],
    interestMax: null as number | null,
    foreignMax: null as number | null,
    issuers: [] as string[],
    nfc: false,
  });
  
  // Assistant mode
  const [assistantMode, setAssistantMode] = useState(false);
  const [assistantAnswers, setAssistantAnswers] = useState<Record<string, string>>({});
  
  const { toast } = useToast();
  const { selectedCards, addCard, removeCard, clearAll } = useComparison();
  const filterBarRef = useRef<HTMLDivElement>(null);
  
  // Apply overrides to cards with useMemo
  const cardsWithOverrides = useMemo(() => {
    // Helper to unwrap value_override objects
    const unwrap = (v: any) => (v && typeof v === 'object' && 'value' in v ? v.value : v);
    
    // Sort overrides by effective_from or created_at so latest wins
    const sortedOverrides = [...overrides].sort((a: any, b: any) => {
      const ta = new Date(a.effective_from || a.created_at || 0).getTime();
      const tb = new Date(b.effective_from || b.created_at || 0).getTime();
      return ta - tb; // oldest first, so later assigns win
    });
    
    const overridesMap = new Map();
    sortedOverrides.forEach((override: any) => {
      if (!override.card_id) return;
      if (!overridesMap.has(override.card_id)) {
        overridesMap.set(override.card_id, {});
      }
      overridesMap.get(override.card_id)[override.field_name] = unwrap(override.value_override);
    });
    
    return cards.map(card => {
      const cardOverrides = overridesMap.get(card.id);
      if (cardOverrides) {
        return { ...card, ...cardOverrides };
      }
      return card;
    });
  }, [cards, overrides]);
  
  // Extract unique issuers for issuer filter
  const uniqueIssuers = useMemo(() => 
    Array.from(new Set(cards.map(c => c.issuer))).sort(),
    [cards]
  );

  // Calculate benefits score for German sorting
  const calculateBenefitsScore = (card: Card) => {
    return (
      (card.annual_fee === 0 ? 2 : 0) +
      ((card.foreign_tx_fee_percent ?? 999) === 0 ? 1 : 0) +
      ((card.travel_insurance || card.travel_health_insurance) ? 1 : 0) +
      ((card.cashback_program || card.bonus_program) ? 1 : 0) +
      (card.nfc_contactless ? 0.2 : 0)
    );
  };

  // Sort cards: sponsored first, then by boost_priority
  const sortedCards = useMemo(() => {
    return [...cardsWithOverrides].sort((a, b) => {
      const aSponsored = sponsoredCardIds.has(a.id);
      const bSponsored = sponsoredCardIds.has(b.id);
      
      if (aSponsored && !bSponsored) return -1;
      if (!aSponsored && bSponsored) return 1;
      
      return b.boost_priority - a.boost_priority;
    });
  }, [cardsWithOverrides, sponsoredCardIds]);

  // Compute filter state for facets
  const filterState: FilterState = useMemo(() => ({
    q: searchTerm,
    chips: {
      travel: selectedFilters.includes('travel'),
      cashbackBonus: false,
      freeAnnual: selectedFilters.includes('no-fee'),

      noForeignFee: selectedFilters.includes('no-foreign-fee'),
      visa: selectedFilters.includes('visa'),
      mastercard: selectedFilters.includes('mastercard'),
      amex: selectedFilters.includes('amex'),
      nfc: advancedFilters.nfc,
      prepaid: selectedFilters.includes('prepaid'),
      withAccount: selectedFilters.includes('with-account'),
      discounts: selectedFilters.includes('discounts'),
      hotline: selectedFilters.includes('hotline'),
    },
    advanced: {
      cardTypes: advancedFilters.cardTypes,
      interestMax: advancedFilters.interestMax,
      foreignMax: advancedFilters.foreignMax,
      issuers: advancedFilters.issuers,
    }
  }), [searchTerm, selectedFilters, advancedFilters]);

  const chipCounts = useMemo(
    () => computeChipCounts(sortedCards, filterState).counts,
    [sortedCards, filterState]
  );

  const cardTypeCoverage = useMemo(
    () => computeCardTypeCoverage(sortedCards, filterState),
    [sortedCards, filterState]
  );

  const availableIssuers = useMemo(
    () => getAvailableIssuers(sortedCards, filterState),
    [sortedCards, filterState]
  );

  const handleFilterToggle = (filter: string) => {
    setSelectedFilters(prev =>
      prev.includes(filter)
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const applyAssistantFilters = (answers: Record<string, string>) => {
    const filters: string[] = [];
    
    // Map answers to filter tags
    if (answers.purpose === 'travel') {
      filters.push('travel');
    }
    if (answers.purpose === 'cashback') {
      filters.push('cashback');
    }
    if (answers.fee === 'no') {
      filters.push('no-fee');
    }
    if (answers.priority === 'lowrate') {
      filters.push('low-rate');
    }
    
    // Apply filters
    setSelectedFilters(filters);
    setAssistantMode(true);
    setAssistantAnswers(answers);
    
    // Scroll to results with offset to show title
    setTimeout(() => {
      const element = filterBarRef.current;
      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  const filteredAndSortedCards = useMemo(() => {
    let result: Card[];

    if (promotedCardsIgnoreFilters) {
      // Extract promoted cards (no filtering)
      const promotedCards = sortedCards.filter(card => sponsoredCardIds.has(card.id));
      
      // Filter regular cards (German logic)
      const regularCards = sortedCards.filter(card => !sponsoredCardIds.has(card.id)).filter(card => {
        // Search filter
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          const searchable = `${card.display_name || card.name} ${card.issuer}`.toLowerCase();
          if (!searchable.includes(q)) return false;
        }

        // Chip filters (German)
        if (selectedFilters.includes('travel')) {
          if (!card.travel_insurance && !card.travel_health_insurance && !card.travel_discount_text) {
            return false;
          }
        }
        if (selectedFilters.includes('no-fee')) {
          if ((card.annual_fee ?? 0) !== 0) return false;
        }
        if (selectedFilters.includes('nfc')) {
          if (!card.nfc_contactless) return false;
        }
        if (selectedFilters.includes('no-foreign-fee')) {
          const fx = (card as any).fx_fee_intl_percent ?? card.foreign_tx_fee_percent;
          if (fx !== 0) return false;
        }
        // Network filters (OR logic - show cards matching ANY selected network)
        const networkFilters = selectedFilters.filter(f => 
          ['visa', 'mastercard', 'amex'].includes(f)
        );
        if (networkFilters.length > 0) {
          const matchesNetwork = networkFilters.some(filter => {
            if (filter === 'visa') return card.network === 'visa';
            if (filter === 'mastercard') return card.network === 'mastercard';
            if (filter === 'amex') return card.network === 'american_express';
            return false;
          });
          if (!matchesNetwork) return false;
        }

        // Prepaid filter
        if (selectedFilters.includes('prepaid')) {
          const cardAny = card as any;
          if (!cardAny.is_prepaid) return false;
        }

        // With Account filter
        if (selectedFilters.includes('with-account')) {
          const cardAny = card as any;
          if (!cardAny.current_account_included) return false;
        }

        // Discounts filter
        if (selectedFilters.includes('discounts')) {
          const cardAny = card as any;
          if (!cardAny.has_discounts && !cardAny.display_config?.hasDiscounts) return false;
        }

        // Hotline filter
        if (selectedFilters.includes('hotline')) {
          const cardAny = card as any;
          if (!cardAny.hotline_available) return false;
        }

        // Advanced filters
        if (advancedFilters.cardTypes.length > 0) {
          if (!card.card_type || !advancedFilters.cardTypes.includes(card.card_type)) {
            return false;
          }
        }

        if (advancedFilters.interestMax !== null) {
          if (card.apr_effective === null || card.apr_effective > advancedFilters.interestMax) {
            return false;
          }
        }

        if (advancedFilters.foreignMax !== null) {
          if (card.foreign_tx_fee_percent === null || card.foreign_tx_fee_percent > advancedFilters.foreignMax) {
            return false;
          }
        }

        if (advancedFilters.issuers.length > 0) {
          if (!advancedFilters.issuers.includes(card.issuer)) return false;
        }

        if (advancedFilters.nfc) {
          if (!card.nfc_contactless) return false;
        }

        return true;
      });

      // Combine: promoted first, then filtered regular cards
      result = [...promotedCards, ...regularCards];
    } else {
      // Respect filters: apply filters to ALL cards (same logic as above)
      result = sortedCards.filter(card => {
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          const searchable = `${card.display_name || card.name} ${card.issuer}`.toLowerCase();
          if (!searchable.includes(q)) return false;
        }

        if (selectedFilters.includes('travel')) {
          if (!card.travel_insurance && !card.travel_health_insurance && !card.travel_discount_text) {
            return false;
          }
        }
        if (selectedFilters.includes('no-fee')) {
          if ((card.annual_fee ?? 0) !== 0) return false;
        }
        if (selectedFilters.includes('nfc')) {
          if (!card.nfc_contactless) return false;
        }
        if (selectedFilters.includes('no-foreign-fee')) {
          const fx = (card as any).fx_fee_intl_percent ?? card.foreign_tx_fee_percent;
          if (fx !== 0) return false;
        }
        // Network filters (OR logic - show cards matching ANY selected network)
        const networkFilters = selectedFilters.filter(f => 
          ['visa', 'mastercard', 'amex'].includes(f)
        );
        if (networkFilters.length > 0) {
          const matchesNetwork = networkFilters.some(filter => {
            if (filter === 'visa') return card.network === 'visa';
            if (filter === 'mastercard') return card.network === 'mastercard';
            if (filter === 'amex') return card.network === 'american_express';
            return false;
          });
          if (!matchesNetwork) return false;
        }

        // Prepaid filter
        if (selectedFilters.includes('prepaid')) {
          const cardAny = card as any;
          if (!cardAny.is_prepaid) return false;
        }

        // With Account filter
        if (selectedFilters.includes('with-account')) {
          const cardAny = card as any;
          if (!cardAny.current_account_included) return false;
        }

        // Discounts filter
        if (selectedFilters.includes('discounts')) {
          const cardAny = card as any;
          if (!cardAny.has_discounts && !cardAny.display_config?.hasDiscounts) return false;
        }

        // Hotline filter
        if (selectedFilters.includes('hotline')) {
          const cardAny = card as any;
          if (!cardAny.hotline_available) return false;
        }

        if (advancedFilters.cardTypes.length > 0) {
          if (!card.card_type || !advancedFilters.cardTypes.includes(card.card_type)) {
            return false;
          }
        }

        if (advancedFilters.interestMax !== null) {
          if (card.apr_effective === null || card.apr_effective > advancedFilters.interestMax) {
            return false;
          }
        }

        if (advancedFilters.foreignMax !== null) {
          if (card.foreign_tx_fee_percent === null || card.foreign_tx_fee_percent > advancedFilters.foreignMax) {
            return false;
          }
        }

        if (advancedFilters.issuers.length > 0) {
          if (!advancedFilters.issuers.includes(card.issuer)) return false;
        }

        if (advancedFilters.nfc) {
          if (!card.nfc_contactless) return false;
        }

        return true;
      });
    }

    // Sort based on selected sort option (German)
    switch (sortBy) {
      case 'recommended':
        result = result.sort((a, b) => {
          const aSponsored = sponsoredCardIds.has(a.id);
          const bSponsored = sponsoredCardIds.has(b.id);
          if (aSponsored && !bSponsored) return -1;
          if (!aSponsored && bSponsored) return 1;
          return calculateBenefitsScore(b) - calculateBenefitsScore(a);
        });
        break;
      case 'low-fee':
        result = result.sort((a, b) => {
          const aSponsored = sponsoredCardIds.has(a.id);
          const bSponsored = sponsoredCardIds.has(b.id);
          if (aSponsored && !bSponsored) return -1;
          if (!aSponsored && bSponsored) return 1;
          const aFee = a.annual_fee ?? 999999;
          const bFee = b.annual_fee ?? 999999;
          return aFee - bFee;
        });
        break;
      case 'low-apr':
        result = result.sort((a, b) => {
          const aSponsored = sponsoredCardIds.has(a.id);
          const bSponsored = sponsoredCardIds.has(b.id);
          if (aSponsored && !bSponsored) return -1;
          if (!aSponsored && bSponsored) return 1;
          const aRate = a.apr_effective ?? 999999;
          const bRate = b.apr_effective ?? 999999;
          return aRate - bRate;
        });
        break;
      case 'benefits':
        result = result.sort((a, b) => {
          const aSponsored = sponsoredCardIds.has(a.id);
          const bSponsored = sponsoredCardIds.has(b.id);
          if (aSponsored && !bSponsored) return -1;
          if (!aSponsored && bSponsored) return 1;
          return calculateBenefitsScore(b) - calculateBenefitsScore(a);
        });
        break;
    }

    // "Was ist dir wichtig?" — narrows and re-ranks the default comparison
    if (selectedIntents.length > 0) {
      result = result.filter((card) => {
        if (promotedCardsIgnoreFilters && sponsoredCardIds.has(card.id)) return true;
        return matchesIntents(card, selectedIntents);
      });
      result = [...result].sort((a, b) => {
        const aSponsored = sponsoredCardIds.has(a.id);
        const bSponsored = sponsoredCardIds.has(b.id);
        if (aSponsored && !bSponsored) return -1;
        if (!aSponsored && bSponsored) return 1;
        const diff = intentScore(b, selectedIntents) - intentScore(a, selectedIntents);
        if (diff !== 0) return diff;
        return calculateBenefitsScore(b) - calculateBenefitsScore(a);
      });
    }

    return result;
  }, [sortedCards, searchTerm, selectedFilters, sortBy, sponsoredCardIds, promotedCardsIgnoreFilters, advancedFilters, selectedIntents]);

  // How many cards each intent would still leave, given the other choices
  const intentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    INTENTS.forEach((intent) => {
      const combo = selectedIntents.includes(intent.key)
        ? selectedIntents
        : [...selectedIntents, intent.key];
      counts[intent.key] = sortedCards.filter((card) => {
        if (promotedCardsIgnoreFilters && sponsoredCardIds.has(card.id)) return true;
        return matchesIntents(card, combo);
      }).length;
    });
    return counts;
  }, [sortedCards, selectedIntents, promotedCardsIgnoreFilters, sponsoredCardIds]);

  // Before the full list arrives, use the real published count so the headline
  // never shows a partial number.
  const checkedCardsCount = allCards ? sortedCards.length : (publishedCardCount ?? sortedCards.length);
  const freshnessLabel = currentMonthYearDE();

  // Smart auto-expand: Show all cards when filters/search result in fewer cards
  useEffect(() => {
    const hasActiveFilters = selectedFilters.length > 0;
    const hasSearchTerm = searchTerm.trim().length > 0;
    
    // Auto-expand if:
    // 1. Active filters and result is < 50 cards
    // 2. Active search and result is < 20 cards
    if ((hasActiveFilters && filteredAndSortedCards.length < 50) || 
        (hasSearchTerm && filteredAndSortedCards.length < 20)) {
      setShowAllCards(true);
    }
    // Reset to default when filters are cleared
    else if (!hasActiveFilters && !hasSearchTerm && showAllCards) {
      setShowAllCards(false);
    }
  }, [filteredAndSortedCards.length, selectedFilters.length, searchTerm]);


  const displayedCards = showAllCards 
    ? filteredAndSortedCards 
    : filteredAndSortedCards.slice(0, defaultCardsShown);

  const remainingCardsCount = filteredAndSortedCards.length - defaultCardsShown;

  // Normalize cards to view models for grid and table views
  const heroViewCards = useMemo(() => {
    return displayedCards.map(card => {
      const core = normalizeDbCard(card);
      return { card, heroData: toHeroCardVM(core, card.logo_url, card) };
    });
  }, [displayedCards]);
  
  const gridViewCards = useMemo(() => {
    return displayedCards.map(card => {
      const core = normalizeDbCard(card);
      return toListCardVM(core);
    });
  }, [displayedCards]);

  const tableViewCards = useMemo(() => {
    return displayedCards.map(card => {
      const core = normalizeDbCard(card);
      return { card, row: toTableRowVM(core) };
    });
  }, [displayedCards]);

  const summaryFacts = homepageFacts ?? {
    totalCards: checkedCardsCount,
    noAnnualFeeCards: sortedCards.filter((card) => card.annual_fee === 0).length,
    lowestAprCard: null,
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header Navigation */}
      <Header
        onNavigate={(page) => {
          if (page === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
        currentPage="home"
      />

      <main>
        {/* Decision hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Kreditkarten-Vergleich {currentYear()}: Die besten Kreditkarten im Vergleich
          </h1>
          <p className="mt-3 max-w-2xl text-base lg:text-lg text-muted-foreground">
            Finde die passende Kreditkarte aus {checkedCardsCount} aktuellen Angeboten.
          </p>
          <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span>Über {checkedCardsCount} Kreditkarten geprüft</span>
            <span aria-hidden="true">·</span>
            <span>Stand {freshnessLabel}</span>
            <span aria-hidden="true">·</span>
            <Link to="/ratgeber" className="text-primary underline underline-offset-4">
              Redaktion Kreditkarten.de
            </Link>
          </p>
        </div>

        <section aria-labelledby="kurz-zusammengefasst" className="mx-auto max-w-7xl px-4 pb-8 pt-6 sm:px-6 lg:px-8">
          <div className="border-l-4 border-primary pl-5 sm:pl-7">
            <h2 id="kurz-zusammengefasst" className="text-xl font-bold text-foreground sm:text-2xl">
              Kreditkarten in Deutschland {currentYear()} – kurz zusammengefasst
            </h2>
            <p className="mt-3 max-w-4xl leading-relaxed text-foreground">
              Wir vergleichen aktuell <strong>{summaryFacts.totalCards} Kreditkarten</strong>. Davon haben{" "}
              <strong>{summaryFacts.noAnnualFeeCards} Karten eine Jahresgebühr von 0 €</strong>.
              {summaryFacts.lowestAprCard && typeof summaryFacts.lowestAprCard.apr === "number" && (
                <> Der niedrigste hinterlegte effektive Jahreszins für eine Kreditkarte mit Teilzahlung beträgt{" "}
                  <strong>{formatPercentDE(summaryFacts.lowestAprCard.apr)}</strong> bei der{" "}
                  <Link to={`/kreditkarten/${summaryFacts.lowestAprCard.slug}`} className="font-semibold text-primary underline underline-offset-4">
                    {summaryFacts.lowestAprCard.name}
                  </Link>.
                </>
              )}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">Redaktionell geprüft {freshnessLabel}.</p>
          </div>
        </section>

        <HomepageComparisonTable
          cards={filteredAndSortedCards.slice(0, 6)}
          offers={offersMap}
          sponsoredIds={sponsoredCardIds}
        />

        {/* Was ist dir wichtig? */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div ref={filterBarRef}>
            <IntentChooser
              selected={selectedIntents}
              onToggle={toggleIntent}
              onReset={() => setIntents([])}
              counts={intentCounts}
              countsReady={!!allCards}
              matchCount={
                allCards || selectedIntents.length > 0
                  ? filteredAndSortedCards.length
                  : (publishedCardCount ?? filteredAndSortedCards.length)
              }
            />
          </div>
        </section>

        {/* Advanced filters — closed by default */}
        <section>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                  Erweiterte Filter
                  <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <Suspense fallback={<Skeleton className="h-40 w-full rounded-lg" />}>
                <ModernFilter
                  searchQuery={searchTerm}
                  onSearchChange={setSearchTerm}
                  selectedFilters={selectedFilters}
                  onFilterToggle={handleFilterToggle}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  advancedFilters={advancedFilters}
                  onAdvancedFiltersChange={setAdvancedFilters}
                  availableIssuers={availableIssuers}
                  chipCounts={chipCounts}
                  cardTypeCoverage={cardTypeCoverage}
                  visibleCount={displayedCards.length}
                  totalCount={filteredAndSortedCards.length}
                  onResetAll={() => {
                    setSelectedFilters([]);
                    setSearchTerm('');
                    setSortBy('recommended');
                    setAdvancedFilters({
                      cardTypes: [],
                      interestMax: null,
                      foreignMax: null,
                      issuers: [],
                      nfc: false,
                    });
                    setAssistantMode(false);
                  }}
                />
                </Suspense>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </section>


        {/* Comparison Dialog */}
        <Suspense fallback={null}>
          {comparisonOpen && isGerman && (
            <CompareDialogDE 
              cards={selectedCards as GermanCompareCard[]}
              open={comparisonOpen} 
              onClose={() => setComparisonOpen(false)}
            />
          )}
          {comparisonOpen && !isGerman && (
            <CompareDialog 
              cards={selectedCards as CreditCard[]}
              open={comparisonOpen} 
              onClose={() => setComparisonOpen(false)}
              creditFrame={50000}
            />
          )}
        </Suspense>

        {/* Comparison Bar */}
        {selectedCards.length > 0 && (
          <Suspense fallback={null}>
            <CompareBar
              selectedCards={selectedCards as any}
              onRemoveCard={removeCard}
              onCompare={() => setComparisonOpen(true)}
              onClear={clearAll}
              labels={isGerman ? {
                title: 'Vergleichen',
                clear: 'Zurücksetzen',
                compare: 'Karten vergleichen'
              } : undefined}
            />
          </Suspense>
        )}

        {/* Cards List */}
        <section className={selectedCards.length > 0 ? "pb-32 md:pb-12" : "pb-12 md:pb-12"}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {loading ? (
              viewMode === 'grid' ? (
                useCompactCards ? (
                  compactCardStyle === 'condensed' ? (
                    // CONDENSED COMPACT SKELETON
                    <div className="grid grid-cols-1 gap-6">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="flex flex-col lg:flex-row min-h-[280px]">
                            <div className="w-full lg:w-2/5 bg-gray-100 p-6 flex items-center justify-center">
                              <Skeleton className="w-56 h-36 rounded-2xl" />
                            </div>
                            <div className="w-full lg:w-3/5 bg-white p-6 space-y-3">
                              <Skeleton className="h-5 w-40" />
                              <Skeleton className="h-4 w-56" />
                              <div className="grid grid-cols-4 gap-2">
                                <Skeleton className="h-14 rounded-lg" />
                                <Skeleton className="h-14 rounded-lg" />
                                <Skeleton className="h-14 rounded-lg" />
                                <Skeleton className="h-14 rounded-lg" />
                              </div>
                              <Skeleton className="h-9 w-32 rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // DEFAULT COMPACT SKELETON
                    <div className="grid grid-cols-1 gap-6">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="flex flex-col lg:flex-row min-h-[320px]">
                            <div className="w-full lg:w-1/2 bg-gray-100 p-8 flex items-center justify-center">
                              <Skeleton className="w-56 h-36 rounded-2xl" />
                            </div>
                            <div className="w-full lg:w-1/2 bg-white p-8 space-y-4">
                              <Skeleton className="h-6 w-48" />
                              <Skeleton className="h-4 w-64" />
                              <div className="grid grid-cols-2 gap-3">
                                <Skeleton className="h-16 rounded-lg" />
                                <Skeleton className="h-16 rounded-lg" />
                                <Skeleton className="h-16 rounded-lg" />
                                <Skeleton className="h-16 rounded-lg" />
                              </div>
                              <Skeleton className="h-10 w-32 rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  // STANDARD GRID SKELETON
                  <div className="space-y-6">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex flex-col lg:flex-row min-h-[400px]">
                          <div className="w-full lg:w-1/2 bg-gray-100 p-12 flex items-center justify-center">
                            <Skeleton className="w-72 h-44 rounded-2xl" />
                          </div>
                          <div className="w-full lg:w-1/2 bg-white p-12 space-y-6">
                            <Skeleton className="h-8 w-64" />
                            <Skeleton className="h-5 w-80" />
                            <div className="grid grid-cols-2 gap-4">
                              <Skeleton className="h-20 rounded-lg" />
                              <Skeleton className="h-20 rounded-lg" />
                              <Skeleton className="h-20 rounded-lg" />
                              <Skeleton className="h-20 rounded-lg" />
                            </div>
                            <Skeleton className="h-12 w-40 rounded-lg" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // TABLE VIEW SKELETON
                <div className="bg-card rounded-xl border border-border overflow-hidden p-8">
                  <div className="space-y-4">
                    {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
                  </div>
                </div>
              )
            ) : filteredAndSortedCards.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Keine Karten entsprechen Ihrer Suche</p>
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  onClick={() => {
                    setSelectedFilters([]);
                    setSearchTerm("");
                    setAdvancedFilters({
                      cardTypes: [],
                      interestMax: null,
                      foreignMax: null,
                      issuers: [],
                      nfc: false,
                    });
                  }}
                >
                  Filter zurücksetzen
                </Button>
              </div>
            ) : (
              viewMode === 'grid' ? (
                useCompactCards ? (
                  compactCardStyle === 'condensed' ? (
                    // CONDENSED COMPACT GRID VIEW - Ultra compact with 4-column metrics
                    <Suspense fallback={
                      <div className="grid grid-cols-1 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                            <div className="flex flex-col lg:flex-row min-h-[280px]">
                              <div className="w-full lg:w-2/5 bg-gray-100 p-6 flex items-center justify-center">
                                <Skeleton className="w-56 h-36 rounded-2xl" />
                              </div>
                              <div className="w-full lg:w-3/5 bg-white p-6 space-y-3">
                                <Skeleton className="h-5 w-40" />
                                <Skeleton className="h-4 w-56" />
                                <div className="grid grid-cols-4 gap-2">
                                  <Skeleton className="h-14 rounded-lg" />
                                  <Skeleton className="h-14 rounded-lg" />
                                  <Skeleton className="h-14 rounded-lg" />
                                  <Skeleton className="h-14 rounded-lg" />
                                </div>
                                <Skeleton className="h-9 w-32 rounded-lg" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    }>
                      <div className="grid grid-cols-1 gap-6">
                        {heroViewCards.map(({ card, heroData }, cardIndex) => (
                          <div 
                            key={card.id} 
                            className="border border-gray-200 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                          >
                            <HeroSplitScreenCondensed
                              card={heroData}
                              cardDetailUrl={`/kreditkarten/${card.slug || card.id}`}
                              isPromoted={sponsoredCardIds.has(card.id)}
                              displayConfigChips={(card as any)?.display_config?.chips || []}
                              isGerman={isGerman}
                              imagePriority={cardIndex < 2}
                              onTitleClick={() => {
                                navigate(`/kreditkarten/${card.slug || card.id}`);
                              }}
                              onCTAClick={async () => {
                                const offerData = offersMap.get(card.id);
                                const affiliateUrl = offerData?.apply_url || card.affiliate_link;
                                if (affiliateUrl) {
                                  // Track in both Supabase and GTM
                                  trackAffiliateClick(card.id, 'grid_view', card.name);
                                  await trackAffiliateClickGTM({
                                    cardId: card.id,
                                    cardName: card.name || '',
                                    issuerName: card.issuer || '',
                                    affiliateUrl: affiliateUrl,
                                    clickSource: 'grid_view',
                                    offerTitle: offerData?.title,
                                    annualFee: card.annual_fee ?? undefined,
                                    effectiveRate: card.apr_effective ?? undefined,
                                  });
                                  handleAffiliateRedirect({
                                    cardId: card.id,
                                    cardName: card.name || '',
                                    issuerName: card.issuer || '',
                                    affiliateUrl: affiliateUrl,
                                    source: 'grid_view',
                                    offerTitle: offerData?.title,
                                    annualFee: card.annual_fee ?? undefined,
                                    effectiveRate: card.apr_effective ?? undefined,
                                  });
                                }
                              }}
                              onLearnMoreClick={() => {
                                navigate(`/kreditkarten/${card.slug || card.id}`);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </Suspense>
                  ) : (
                  // DEFAULT COMPACT GRID VIEW - Scaled-down HeroSplitScreen
                  <Suspense fallback={
                    <div className="grid grid-cols-1 gap-6">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="flex flex-col lg:flex-row min-h-[320px]">
                            <div className="w-full lg:w-1/2 bg-gray-100 p-8 flex items-center justify-center">
                              <Skeleton className="w-56 h-36 rounded-2xl" />
                            </div>
                            <div className="w-full lg:w-1/2 bg-white p-8 space-y-4">
                              <Skeleton className="h-6 w-48" />
                              <Skeleton className="h-4 w-64" />
                              <div className="grid grid-cols-2 gap-3">
                                <Skeleton className="h-16 rounded-lg" />
                                <Skeleton className="h-16 rounded-lg" />
                                <Skeleton className="h-16 rounded-lg" />
                                <Skeleton className="h-16 rounded-lg" />
                              </div>
                              <Skeleton className="h-10 w-32 rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }>
                    <div className="grid grid-cols-1 gap-6">
                      {heroViewCards.map(({ card, heroData }, cardIndex) => (
                        <div 
                          key={card.id} 
                          className="border border-gray-200 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                        >
                          <HeroSplitScreen
                            card={heroData}
                            cardDetailUrl={`/kreditkarten/${card.slug || card.id}`}
                            isPromoted={sponsoredCardIds.has(card.id)}
                            hasAffiliateLink={!!(offersMap.get(card.id)?.apply_url || card.affiliate_link)}
                            ctaHref={offersMap.get(card.id)?.apply_url || card.affiliate_link || undefined}
                            displayConfigChips={(card as any)?.display_config?.chips || []}
                            isGerman={isGerman}
                            isCompact={true}
                            imagePriority={cardIndex < 2}
                            onImageClick={() => {
                              navigate(`/kreditkarten/${card.slug || card.id}`);
                            }}
                            onTitleClick={() => {
                              navigate(`/kreditkarten/${card.slug || card.id}`);
                            }}
                            onCTAClick={async () => {
                              const offerData = offersMap.get(card.id);
                              const affiliateUrl = offerData?.apply_url || card.affiliate_link;
                              if (affiliateUrl) {
                                // Track in both Supabase and GTM
                                trackAffiliateClick(card.id, 'grid_view', card.name);
                                await trackAffiliateClickGTM({
                                  cardId: card.id,
                                  cardName: card.name || '',
                                  issuerName: card.issuer || '',
                                  affiliateUrl: affiliateUrl,
                                  clickSource: 'grid_view',
                                  offerTitle: offerData?.title,
                                  annualFee: card.annual_fee ?? undefined,
                                  effectiveRate: card.apr_effective ?? undefined,
                                });
                                handleAffiliateRedirect({
                                  cardId: card.id,
                                  cardName: card.name || '',
                                  issuerName: card.issuer || '',
                                  affiliateUrl: affiliateUrl,
                                  source: 'grid_view',
                                  offerTitle: offerData?.title,
                                  annualFee: card.annual_fee ?? undefined,
                                  effectiveRate: card.apr_effective ?? undefined,
                                });
                              }
                            }}
                            onLearnMoreClick={() => {
                              navigate(`/kreditkarten/${card.slug || card.id}`);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </Suspense>
                  )
                ) : (
                  // STANDARD GRID VIEW - Hero Cards (1 per row)
                  <Suspense fallback={
                    <div className="space-y-6">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="flex flex-col lg:flex-row min-h-[400px]">
                            <div className="w-full lg:w-1/2 bg-gray-100 p-12 flex items-center justify-center">
                              <Skeleton className="w-72 h-44 rounded-2xl" />
                            </div>
                            <div className="w-full lg:w-1/2 bg-white p-12 space-y-6">
                              <Skeleton className="h-8 w-64" />
                              <Skeleton className="h-5 w-80" />
                              <div className="grid grid-cols-2 gap-4">
                                <Skeleton className="h-20 rounded-lg" />
                                <Skeleton className="h-20 rounded-lg" />
                                <Skeleton className="h-20 rounded-lg" />
                                <Skeleton className="h-20 rounded-lg" />
                              </div>
                              <Skeleton className="h-12 w-40 rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }>
                    <div className="space-y-6">
                      {heroViewCards.map(({ card, heroData }, cardIndex) => (
                        <div 
                          key={card.id} 
                          className="border border-gray-200 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                        >
                    <HeroSplitScreen
                      card={heroData}
                      cardDetailUrl={`/kreditkarten/${card.slug || card.id}`}
                      isPromoted={sponsoredCardIds.has(card.id)}
                      hasAffiliateLink={!!(offersMap.get(card.id)?.apply_url || card.affiliate_link)}
                      ctaHref={offersMap.get(card.id)?.apply_url || card.affiliate_link || undefined}
                      displayConfigChips={(card as any)?.display_config?.chips || []}
                      isGerman={isGerman}
                      imagePriority={cardIndex < 2}
                      onImageClick={() => {
                        navigate(`/kreditkarten/${card.slug || card.id}`);
                      }}
                      onTitleClick={() => {
                        navigate(`/kreditkarten/${card.slug || card.id}`);
                      }}
                      onCTAClick={async () => {
                        const affiliateUrl = offersMap.get(card.id)?.apply_url || card.affiliate_link;
                        if (affiliateUrl) {
                          // Track in both Supabase and GTM
                          trackAffiliateClick(card.id, 'grid_view', card.name);
                          await trackAffiliateClickGTM({
                            cardId: card.id,
                            cardName: card.display_name || card.name,
                            issuerName: card.issuer,
                            affiliateUrl: affiliateUrl,
                            clickSource: 'grid_view',
                            offerTitle: offersMap.get(card.id)?.title,
                            annualFee: card.annual_fee ?? undefined,
                            effectiveRate: card.apr_effective ?? undefined,
                          });
                          handleAffiliateRedirect({
                            cardId: card.id,
                            cardName: card.display_name || card.name,
                            issuerName: card.issuer,
                            affiliateUrl: affiliateUrl,
                            source: 'grid_view',
                            offerTitle: offersMap.get(card.id)?.title,
                            annualFee: card.annual_fee ?? undefined,
                            effectiveRate: card.apr_effective ?? undefined,
                          });
                        }
                      }}
                      onLearnMoreClick={() => {
                        navigate(`/kreditkarten/${card.slug || card.id}`);
                      }}
                    />
                          
                          {/* HIDDEN: Comparison Checkbox - Below Hero Card */}
                          <div className="hidden border-t border-gray-200 p-4 bg-gray-50">
                            <div className="flex items-center justify-between">
                              <img
                                src="https://fatcamp.io/kredittkort.com/images/finansportalen/fp_data-levert-av_sv-hv.svg"
                                alt="Data levert av Finansportalen"
                                className="h-10 w-auto"
                                loading="lazy"
                              />
                              <label className="flex items-center gap-2 cursor-pointer group">
                                <Checkbox
                                  checked={selectedCards.some(c => c.id === card.id)}
                                  onCheckedChange={() => {
                                    const isCurrentlySelected = selectedCards.some(c => c.id === card.id);
                                    if (isCurrentlySelected) {
                                      removeCard(card.id);
                                    } else {
                                      addCard(adaptSupabaseCardToCompareCard(card, sponsoredCardIds.has(card.id)));
                                    }
                                  }}
                                  disabled={selectedCards.length >= 3 && !selectedCards.some(c => c.id === card.id)}
                                />
                                <span className="text-sm text-gray-700 group-hover:text-primary">Vergleichen</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Suspense>
                )
              ) : (
                // TABLE VIEW
                <Suspense fallback={
                  <div className="bg-card rounded-xl border border-border overflow-hidden p-8">
                    <div className="space-y-4">
                      {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
                    </div>
                  </div>
                }>
                  <div className="bg-card rounded-xl border border-border overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12"></TableHead>
                            <TableHead>Karte</TableHead>
                            <TableHead>Teilzahlung</TableHead>
                            <TableHead>Jahresgebühr</TableHead>
                            <TableHead>Fremdwährung</TableHead>
                            <TableHead>Bargeld</TableHead>
                            <TableHead>Vorteile</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                       </TableHeader>
                       <TableBody>
                        {tableViewCards.map(({ card, row }) => (
                          <CardTableRow
                                key={card.id}
                                row={row}
                                rawCard={card}
                                offer={offersMap.get(card.id)}
                                creditFrame={50000}
                                isSelected={selectedCards.some(c => c.id === card.id)}
                                onToggleCompare={() => {
                                  const isCurrentlySelected = selectedCards.some(c => c.id === card.id);
                                  if (isCurrentlySelected) {
                                    removeCard(card.id);
                                  } else {
                                    const compareCard = isGerman
                                      ? adaptSupabaseCardToGermanCompareCard(card, sponsoredCardIds.has(card.id))
                                      : adaptSupabaseCardToCompareCard(card, sponsoredCardIds.has(card.id));
                                    addCard(compareCard);
                                  }
                                }}
                                compareDisabled={selectedCards.length >= 3 && !selectedCards.some(c => c.id === card.id)}
                                isSponsored={sponsoredCardIds.has(card.id)}
                                sponsoredLabel={sponsoredLabels.get(card.id)}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </Suspense>
              )
            )}

            {/* Vis flere button */}
            {!showAllCards && remainingCardsCount > 0 && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setShowAllCards(true)}
                  className="gap-2"
                >
                  Zeige {remainingCardsCount} weitere Karten
                </Button>
              </div>
          )}
        </div>
      </section>

        {/* Homepage Content Sections */}
        {showContentSections && !loading && (
          <div className="below-fold max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
            <HomepageNavigation />
            
            {homepageContent
              .filter(c => c.section_type !== 'faq' && c.is_enabled)
              .sort((a, b) => a.display_order - b.display_order)
              .map((section) => (
                <HomepageContentSection
                  key={section.id}
                  id={section.section_id || section.section_type}
                  title={section.title || undefined}
                  content={section.content}
                />
              ))}
          </div>
        )}

        {/* Dynamic Sections - Before FAQ */}
        <Suspense fallback={null}>
          <div className="below-fold">
          <SectionRenderer
            currentPage="homepage"
            sectionsFilter={(section) => section.position < 2}
            onViewDetails={(slug) => navigate(`/kreditkarten/${slug}`)}
            onViewAllCards={() => {
              const element = filterBarRef.current;
              if (element) {
                const yOffset = -100;
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
              }
            }}
          />
          </div>
        </Suspense>

        {/* FAQ Section */}
        <div className="below-fold max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Suspense fallback={null}>
            <HomepageFAQ 
              faqs={HOMEPAGE_FAQ}
              subtitle="Antworten auf wichtige Fragen zu Kosten, Kartentypen und Nutzung."
            />
          </Suspense>
        </div>

        {/* Dynamic Sections - After FAQ */}
        <Suspense fallback={null}>
          <div className="below-fold">
          <SectionRenderer
            currentPage="homepage"
            sectionsFilter={(section) => section.position >= 2}
            onViewDetails={(slug) => navigate(`/kreditkarten/${slug}`)}
            onViewAllCards={() => {
              const element = filterBarRef.current;
              if (element) {
                const yOffset = -100;
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
              }
            }}
          />
          </div>
        </Suspense>
      </main>

      <Footer 
        onNavigate={(page) => {
          if (page === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (page === 'article') {
            window.location.href = '/artikler';
          }
        }}
      />
    </div>
  );
};

export default Index;
